/* eslint-disable no-nested-ternary */
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { useErrorContext } from '@qpr/client-library';
import React, { useEffect, useState } from 'react';
import DataTable from './DataTable';
import { Data, DataTableState } from './datatable-state';
import createDataTableViewModel, { DataTableViewModelType } from './datatable-view-model';

const PREFIX = 'DataTableView';

const classes = {
  link: `${PREFIX}-link`,
  cell: `${PREFIX}-cell`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.link}`]: {
    color: theme.palette.text.primary,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },

  [`& .${classes.cell}`]: {
    borderBottomColor: alpha(theme.palette.background.default, 0.12)
  }
}));

interface DataTableProps {
  query?: string;
  taskType?: string;
  title?: string;
}

const DataTableView = ({ query = '', taskType = 'Activity', title = '' }: DataTableProps) => {
  const [data, setData] = useState<DataTableState>();
  const { setError } = useErrorContext();

  const theme = useTheme();

  const viewModel: DataTableViewModelType = createDataTableViewModel({
    id: query || '',
    taskType
  });

  useEffect(() => {
    const initData = async () => {
      const viewModelData = await viewModel.getData();
      if (viewModelData.error?.message) {
        setError(viewModelData.error);
        return;
      }
      setData(viewModelData);
    };
    initData();
  }, []);

  const columns = [
    {
      title: 'Role',
      field: 'role',
      cellStyle: {
        borderBottomColor: alpha(theme.palette.background.default, 0.12)
      }
    },
    {
      title: 'Tasks',
      field: 'tasks',
      cellStyle: {
        borderBottomColor: alpha(theme.palette.background.default, 0.12)
      }
    },
    {
      title: 'Documentation',
      field: 'informationitems',
      cellStyle: {
        borderBottomColor: alpha(theme.palette.background.default, 0.12)
      },
      customSort: (a: Data, b: Data) => {
        return (a.informationitems?.name || '').localeCompare(b.informationitems?.name || '');
      },
      render: (rowData: Data) =>
        rowData.informationitems?.link && rowData.informationitems?.link !== '' ? (
          <a
            className={classes.link}
            href={rowData.informationitems.link}
            rel="noopener noreferrer"
            target="_blank"
          >
            {rowData.informationitems.name}
          </a>
        ) : (
          rowData.informationitems?.name || ''
        )
    }
  ];

  return data?.data ? (
    <DataTable data={data?.data} columns={columns} title={title} />
  ) : (
    <Root data-testid="loadingIndicator" style={{ width: '40px', margin: 'auto' }}>
      <CircularProgress />
    </Root>
  );
};

export default DataTableView;
